import { computed, nextTick, shallowRef, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useResizeObserver } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { calcScreenDPI, getHeightForA4Page, isInViewport, throttle } from '../utils/helpers';
import { useBuilder } from './builder.store';
import { useBuilderStyles } from './builder-style.store';

export const useBuilderToolsStore = defineStore('builder-tools', () => {
	const nrOfPages = shallowRef(1);
	const currentPage = shallowRef(1);
	const route = useRoute();

	const builder = useBuilder();
	const { data, loaded } = storeToRefs(builder);
	const builderStyles = useBuilderStyles();

	// Margin Setup saves what was typed, so margins can be strings; the page padding reads them as numbers.
	const topMargin = computed(() => Number(builderStyles.topMargin) || 0);
	const bottomMargin = computed(() => Number(builderStyles.bottomMargin) || 0);

	// Content that fits between one page's top and bottom margins, unzoomed. The PDF prints
	// those margins on every page (EN-1232), so every page holds this much.
	const pageContentHeight = computed(() => {
		return getHeightForA4Page(calcScreenDPI()) - topMargin.value - bottomMargin.value;
	});

	const calculateNrOfPages = () => {
		const divHeight = document
			?.querySelector('.builder-content')
			?.getBoundingClientRect()?.height || 0;
		const contentHeight = (divHeight / (builderStyles.zoom / 100)) - topMargin.value - bottomMargin.value;

		nrOfPages.value = Math.max(1, Math.ceil(contentHeight / pageContentHeight.value));
	};

	// Offset from the top of .builder-content, at the current zoom, where `page` begins.
	const pageBreakTop = (page) => {
		if (page <= 1) {return 0;}

		return (topMargin.value + ((page - 1) * pageContentHeight.value)) * (builderStyles.zoom / 100);
	};

	// Recount whenever the page changes size: TipTap filling in content after load, zoom, margins,
	// edits and late font swaps. A one-off count right after load measured empty editors (EN-1232).
	// border-box, because a top/bottom margin change only moves the padding.
	const observeBuilderContent = (target) => {
		useResizeObserver(target, () => calculateNrOfPages(), { box: 'border-box' });
	};

	const scrollToPage = (page) => {
		if (page > nrOfPages.value || page < 1) {return;}
		const el = document.querySelector(`.page-${  page}`);

		if (!el || !document.contains(el)) {return;}

		el.scrollIntoView({ behavior: 'smooth' });

		setTimeout(() => (currentPage.value = page), 100);
	};

	const checkIfSomePageIsInViewport = () => {
		for (let page = 1; page <= nrOfPages.value; page++) {
			if (isInViewport(document.querySelector(`.page-${  page}`))) {
				currentPage.value = page;
			}
		}
	};
    
	const scrollEventListener = () => {
		document.addEventListener('wheel', () => {
			throttle(checkIfSomePageIsInViewport, 250);
		});
	};
    
	const isOnCoverLetterPage = computed(() => {
		return route?.name?.startsWith('cover-letters');
	});

	watch(data, async () => {
		if (!loaded.value) {return;}
        
		await nextTick(() => {
			calculateNrOfPages();
		});
	}, { deep: true });

	watch(loaded, async ( newLoaded ) => {
		if (!newLoaded) {return;}
        
		await nextTick(() => {
			calculateNrOfPages();
		});
	});

	return {
		nrOfPages,
		currentPage,
		isOnCoverLetterPage,
        
		calculateNrOfPages,
		pageBreakTop,
		observeBuilderContent,
		scrollToPage,
		checkIfSomePageIsInViewport,

		scrollEventListener
	};
});
